// Window/level for a grayscale image of deeper samples than a screen shows: an
// X-ray, as the converter stores it (convert sources/radiograph.py) — a 16-bit
// MONOCHROME2 pyramid carrying the device's windows on every level.
//
// A window is the slice of stored values spread from black to white: its
// centre (the level) and its width. The engine draws it on the GPU
// (DmvEngine.setWindow → DMV's limitValues), so moving it re-fetches and
// re-decodes nothing. Kept apart from the engine and the controls so the maths
// is tested on its own.

import { getNumber, getSequence, getString, Tag, type DicomJson } from '../../data/dicomTags';

/** A window in the stored values of the image as the viewer has it. */
export interface Window {
  center: number;
  width: number;
}

/**
 * How many base-level pixels hold each run of stored values: the Image
 * Histogram module the converter writes (PS3.3 C.11.5). Bin i counts the
 * values first + i·width up to first + (i+1)·width − 1.
 */
export interface Histogram {
  first: number;
  width: number;
  counts: number[];
}

/** A window the file names: the device's, or one of its alternatives. */
export interface WindowPreset {
  name: string;
  window: Window;
}

export interface Windowing {
  /** The file's own windows, the first (the device's choice) first. */
  presets: WindowPreset[];
  /** The image's own range of values, smallest to largest. */
  full: Window;
  /** The largest value the stored bits hold. */
  top: number;
  /** The largest value a window may reach: 2^BitsAllocated − 1, where DMV clips one. */
  ceiling: number;
  /**
   * The X-ray was MONOCHROME1 (minimum white) and the converter turned its
   * values over to MONOCHROME2. The picture is the same; readouts say the
   * file's own numbers, so they match what the device and the Info panel say.
   */
  flipped: boolean;
  /** A For Processing image (raw detector data): it opens on its full range. */
  forProcessing: boolean;
  /** The base level's histogram, or null for an X-ray converted before there was one. */
  histogram: Histogram | null;
  /** Width over height of the entire image, which sizes the window box like the minimap. */
  aspect: number | null;
  /** The window the engine opened the image on (openingWindow); the device's (initialWindow) when absent. */
  opening?: Window;
}

/** What the converter names a window it made for an image that had none. */
const MADE_FULL_RANGE = 'FULL RANGE';

/** A radiograph as the viewer is handed one: an X-ray's pyramid, which keeps the DX modality. */
export const isRadiograph = (ds: DicomJson | undefined): boolean => getString(ds, Tag.Modality)?.toUpperCase() === 'DX';

const numbers = (ds: DicomJson | undefined, tag: string): number[] =>
  ((ds?.[tag] as { Value?: unknown[] } | undefined)?.Value ?? []).map(Number).filter((v) => Number.isFinite(v));
const strings = (ds: DicomJson | undefined, tag: string): string[] =>
  ((ds?.[tag] as { Value?: unknown[] } | undefined)?.Value ?? []).map((v) => String(v ?? '').trim());

/** Whether the converter turned this image's values over (its Original Attributes say MONOCHROME1). */
function wasMonochrome1(ds: DicomJson | undefined): boolean {
  const change = (ds?.[Tag.OriginalAttributesSequence] as { Value?: DicomJson[] } | undefined)?.Value?.[0];
  const original = (change?.[Tag.ModifiedAttributesSequence] as { Value?: DicomJson[] } | undefined)?.Value?.[0];
  return getString(original, Tag.PhotometricInterpretation) === 'MONOCHROME1';
}

/** The histogram one of `levels` carries (the base level), or null when none does. */
export function histogramOf(levels: readonly (DicomJson | undefined)[]): Histogram | null {
  for (const ds of levels) {
    const item = getSequence(ds, Tag.HistogramSequence)[0];
    if (!item) continue;
    const first = getNumber(item, Tag.HistogramFirstBinValue);
    const width = getNumber(item, Tag.HistogramBinWidth) ?? 1;
    const counts = numbers(item, Tag.HistogramData);
    if (first === undefined || !(width > 0) || counts.length === 0) continue;
    return { first, width, counts };
  }
  return null;
}

/**
 * The windowing a grayscale image offers, from one of its levels (every level
 * carries the same windows; the histogram is the base's, looked for in
 * `levels`), or null for one with nothing to window: colour, or 8-bit.
 */
export function windowingOf(ds: DicomJson | undefined, levels: readonly (DicomJson | undefined)[] = [ds]): Windowing | null {
  if (getString(ds, Tag.PhotometricInterpretation) !== 'MONOCHROME2') return null;
  const allocated = getNumber(ds, Tag.BitsAllocated) ?? 8;
  if (allocated <= 8) return null;
  const top = 2 ** Math.min(getNumber(ds, Tag.BitsStored) ?? allocated, allocated) - 1;
  const smallest = getNumber(ds, Tag.SmallestImagePixelValue);
  const largest = getNumber(ds, Tag.LargestImagePixelValue);
  const lo = smallest !== undefined && largest !== undefined && largest > smallest ? smallest : 0;
  const hi = smallest !== undefined && largest !== undefined && largest > smallest ? largest : top;
  const centres = numbers(ds, Tag.WindowCenter);
  const widths = numbers(ds, Tag.WindowWidth);
  const names = strings(ds, Tag.WindowCenterWidthExplanation);
  const presets: WindowPreset[] = [];
  centres.forEach((center, i) => {
    const width = widths[i];
    if (!(width > 0)) return;
    const name = names[i] || (centres.length > 1 ? `Device ${i + 1}` : 'Device');
    if (name.toUpperCase() === MADE_FULL_RANGE) return;
    presets.push({ name, window: { center, width } });
  });
  return {
    presets,
    full: { center: (lo + hi + 1) / 2, width: hi - lo + 1 },
    top,
    ceiling: 2 ** allocated - 1,
    flipped: wasMonochrome1(ds),
    forProcessing: getString(ds, Tag.PresentationIntentType)?.toUpperCase() === 'FOR PROCESSING',
    histogram: histogramOf(levels),
    aspect: aspectOf(ds),
  };
}

function aspectOf(ds: DicomJson | undefined): number | null {
  const cols = getNumber(ds, Tag.TotalPixelMatrixColumns) ?? getNumber(ds, Tag.Columns);
  const rows = getNumber(ds, Tag.TotalPixelMatrixRows) ?? getNumber(ds, Tag.Rows);
  return cols && rows ? cols / rows : null;
}

/** The window an image opens on: the device's, or its full range when it has none or is raw data. */
export const initialWindow = (w: Windowing): Window =>
  fitted(w.forProcessing || w.presets.length === 0 ? w.full : w.presets[0].window, w);

/**
 * The window an X-ray opens on: the automatic one when the person chose it in
 * Settings (user, 2026-10-16, window/xrayAutoWindow) and the image has a
 * histogram to find it from, else the device's. Reset still goes to the device's.
 */
export function openingWindow(w: Windowing, auto: boolean): Window {
  const found = auto && w.histogram ? autoWindow(w.histogram) : null;
  return found ? fitted(found, w) : initialWindow(w);
}

/** The window an engine opened the image on (Windowing.opening), as the controls first show it. */
export const openedOn = (w: Windowing): Window => w.opening ?? initialWindow(w);

/**
 * The window as DMV can draw it. DMV clips a window's ends at 0 and at
 * 2^BitsAllocated − 1 (setOpticalPathStyle), which would bend it away from what
 * the readout says: so the level stays where a window of width 1 still fits,
 * and the width stops where the window would cross either end.
 */
export function fitted(win: Window, w: Windowing): Window {
  const center = Math.min(Math.max(win.center, 0.5), w.ceiling + 0.5);
  const room = 2 * Math.min(center, w.ceiling + 1 - center);
  return { center, width: Math.min(Math.max(win.width, 1), Math.max(1, room)) };
}

/**
 * DMV's [black, white] stored values for a window (PS3.3 C.11.2.1.2.1, as
 * monochrome.ts uses it), swapped when the picture is inverted: DMV maps
 * linearly from the first to the second, so a reversed pair runs white to black.
 */
export function limitsOf(w: Window, inverted: boolean): [number, number] {
  const black = w.center - 0.5 - (w.width - 1) / 2;
  const white = w.center - 0.5 + (w.width - 1) / 2;
  return inverted ? [white, black] : [black, white];
}

// Auto: the window that shows the specimen best (user, 2026-10-16). Measured
// on real specimens against the device's own window: of windows cut anywhere
// between the specimen's percentiles, the one whose 256 greys carry the most
// information about it. Too narrow piles its pixels on pure black and white,
// too wide crowds them into a few greys; both lose information. It settled on
// about 10% clipped, the trade the device made, and moved the cut per image.

/**
 * Running totals of `counts`, bins of `h`, and how many of those pixels hold
 * a stored value below v: a bin spread evenly over its values. What the
 * curves and the auto window both count by.
 */
function cumulative(h: Pick<Histogram, 'first' | 'width'>, counts: readonly number[]) {
  const n = counts.length;
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i] + counts[i];
  const below = (v: number) => {
    const b = Math.min(n, Math.max(0, (v - h.first) / h.width));
    const i = Math.floor(b);
    return prefix[i] + (i < n ? (b - i) * counts[i] : 0);
  };
  return { prefix, below };
}

/** Where a window may be cut, as a percentile of the specimen, at either end. */
const AUTO_CUTS = [0, 0.5, 1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];
/** The open field ends where its spike falls to this share of its peak... */
const OPEN_FIELD_FOOT = 0.01;
/** ...and is only set aside when that narrow (a share of the image's range): a broad peak is tissue. */
const OPEN_FIELD_NARROW = 0.1;

/**
 * The histogram's bins less the open field, where nothing stood between the
 * beam and the detector: a tall, narrow spike of near-identical values, 71% of
 * a specimen's frame. Without that, any window fitted to the counts fits the
 * empty background.
 */
export function specimenCounts(h: Histogram): number[] {
  const counts = h.counts.slice();
  let peak = 0;
  for (let i = 1; i < counts.length; i++) if (counts[i] > counts[peak]) peak = i;
  const foot = counts[peak] * OPEN_FIELD_FOOT;
  let a = peak;
  let b = peak;
  while (a > 0 && counts[a - 1] > foot) a--;
  while (b < counts.length - 1 && counts[b + 1] > foot) b++;
  if ((b - a + 1) / counts.length > OPEN_FIELD_NARROW) return counts;
  for (let i = a; i <= b; i++) counts[i] = 0;
  return counts;
}

/**
 * The window that shows the specimen with the most information, from the
 * stored histogram alone: nothing to decode, about a millisecond. Null with
 * nothing to show (an empty or single-valued histogram).
 */
export function autoWindow(h: Histogram): Window | null {
  const counts = specimenCounts(h);
  const n = counts.length;
  const { prefix, below } = cumulative(h, counts);
  const total = prefix[n];
  if (!(total > 0)) return null;
  // The value below which a share q of the specimen lies: in a bin that holds
  // some of it, so the 0% cut is the specimen's darkest value, not the start
  // of bins it left empty (the open field, set aside).
  const at = (q: number) => {
    const target = q * total;
    let i = 0;
    while (i < n - 1 && (counts[i] === 0 || prefix[i + 1] < target)) i++;
    const inBin = counts[i] > 0 ? (target - prefix[i]) / counts[i] : 0;
    return h.first + (i + Math.min(1, Math.max(0, inBin))) * h.width;
  };
  const los = AUTO_CUTS.map((q) => at(q / 100));
  const his = AUTO_CUTS.map((q) => at(1 - q / 100));
  let best: { info: number; lo: number; hi: number } | null = null;
  for (const lo of los) {
    for (const hi of his) {
      if (!(hi > lo)) continue;
      // The specimen through this window, in 256 greys: what it clips lands on black or white.
      let info = 0;
      const step = (hi - lo) / 256;
      for (let k = 0; k < 256; k++) {
        let c = below(lo + (k + 1) * step) - below(lo + k * step);
        if (k === 0) c += below(lo);
        if (k === 255) c += total - below(hi);
        if (c > 0) info -= (c / total) * Math.log2(c / total);
      }
      if (!best || info > best.info) best = { info, lo, hi };
    }
  }
  return best && { center: (best.lo + best.hi) / 2, width: best.hi - best.lo };
}

/** CSS px of drag that double (or halve) the width, and that move the level one width. */
export const DRAG_DOUBLING_PX = 100;
export const DRAG_SHIFT_PX = 100;

/**
 * A window dragged by (dx, dy) CSS px: right widens it (softer), left narrows
 * it (more contrast); down raises the level (darker), up lowers it (brighter)
 * — the OHIF/Cornerstone and RadiAnt directions. In proportion to the window
 * the drag started from, so the device's window, a sliver of a 14-bit range,
 * steers as easily as the entire range: a pane-wide scale took a 266-wide
 * window to 1 in 130 px.
 */
export function dragged(start: Window, dx: number, dy: number): Window {
  return {
    center: start.center + (dy / DRAG_SHIFT_PX) * start.width,
    width: Math.max(1, start.width * 2 ** (dx / DRAG_DOUBLING_PX)),
  };
}

// The window box (features/study/WindowBox): a close-up of the histogram
// around the window, which stays put in its middle third while the counts
// slide under it, and the entire range as a strip.

/** The stored values the close-up spans: three window-widths, the level in the middle. */
export const closeUpOf = (win: Window): [number, number] => [win.center - 1.5 * win.width, win.center + 1.5 * win.width];

/** The image's own range of stored values, [smallest, largest + 1). */
export const rangeOf = (w: Windowing): [number, number] => [w.full.center - w.full.width / 2, w.full.center + w.full.width / 2];

/**
 * Where a drag may take a window: the image's own range, as on a slider, or
 * as far past it as the window already reached (a device window wider than
 * the image's values keeps its reach). Unbounded, a drag went on for ever and
 * past the display's own ends squeezed the window (user, 2026-10-16).
 */
export function boundsOf(w: Windowing, start: Window): [number, number] {
  const [lo, hi] = rangeOf(w);
  return [Math.min(lo, start.center - start.width / 2), Math.max(hi, start.center + start.width / 2)];
}

/** A window held inside `bounds` by the drag on the image: its level within them, no wider than they are. */
export function within(win: Window, [b0, b1]: [number, number]): Window {
  return { center: Math.min(Math.max(win.center, b0), b1), width: Math.min(win.width, b1 - b0) };
}

/**
 * The counts of the stored values [lo, hi) in `n` equal columns: each the
 * pixels its values hold, a bin spread evenly over its width where a column
 * takes only part of it. Values outside the histogram hold none.
 */
export function histogramColumns(h: Histogram, lo: number, hi: number, n: number): number[] {
  const { below } = cumulative(h, h.counts);
  const step = (hi - lo) / n;
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(Math.max(0, below(lo + (i + 1) * step) - below(lo + i * step)));
  return out;
}

/** The level in the file's own numbers: for an X-ray stored turned over, mirrored back. */
export const sourceLevel = (win: Window, w: Windowing): number => (w.flipped ? w.top + 1 - win.center : win.center);

/** The window's width and level as the device would say them, ready to show; whole numbers from 100 up when `compact`. */
export function readoutParts(win: Window, w: Windowing, compact = false): { width: string; level: string } {
  const say = (v: number) => (compact && Math.abs(v) >= 100 ? String(Math.round(v)) : tidy(v));
  return { width: say(win.width), level: say(sourceLevel(win, w)) };
}

/** A window in the file's own numbers: its width, and its level as the device says it. */
export interface SourceWindow {
  width: number;
  level: number;
}

/** A window as its file says it: what copying keeps (features/study/windowClipboard). */
export const sourceWindow = (win: Window, w: Windowing): SourceWindow => ({ width: win.width, level: sourceLevel(win, w) });

/** A window the file's own numbers name, in this image's stored values: what pasting sets. */
export const storedWindow = (s: SourceWindow, w: Windowing): Window => ({
  width: s.width,
  center: w.flipped ? w.top + 1 - s.level : s.level,
});

/** `W 266 · L 219`: the window as the device would say it. */
export function readout(win: Window, w: Windowing): string {
  const { width, level } = readoutParts(win, w);
  return `W ${width} · L ${level}`;
}

function tidy(v: number): string {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}
